/**
 * Web Push client helpers  -  registers service worker, subscribes the user
 * via VAPID, and persists the subscription on the SpectraLearn backend.
 *
 * `ensurePushSubscription` THROWS a typed `PushSubscribeError` on failure so
 * the calling UI can show a useful diagnostic (incognito mode, denied perm,
 * push service unreachable, etc.) instead of a generic "Could not subscribe".
 */
import api from "@/lib/api";

const SW_URL = "/sw.js";

export class PushSubscribeError extends Error {
  constructor(code, message, cause) {
    super(message);
    this.name = "PushSubscribeError";
    this.code = code;
    this.cause = cause;
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function registerServiceWorker() {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.register(SW_URL, { scope: "/" });
    return reg;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("SW register failed", err);
    return null;
  }
}

export async function currentPushSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

/**
 * Translate a browser push-subscribe DOMException into a friendly message
 * pointing at the most likely root cause. Covers the four real-world
 * failure modes we hit in production support tickets.
 */
function diagnoseSubscribeError(err) {
  const name = err?.name || "";
  const msg  = (err?.message || "").toLowerCase();

  if (name === "NotAllowedError" || msg.includes("permission denied")) {
    return new PushSubscribeError(
      "permission_denied",
      "Browser blocked the request. Allow notifications for spectralearn.co in your browser settings, then try again.",
      err,
    );
  }
  if (name === "AbortError" && msg.includes("permission")) {
    // Chrome incognito + some corporate firewall setups
    return new PushSubscribeError(
      "private_or_blocked",
      "Push is not available here. This usually means private/incognito mode, a strict corporate firewall, or a browser flag is blocking the Google push service. Try a normal browser window.",
      err,
    );
  }
  if (name === "AbortError") {
    return new PushSubscribeError(
      "push_service_unreachable",
      "Your browser couldn't reach the push service. Check your network connection and try again.",
      err,
    );
  }
  if (name === "InvalidStateError") {
    return new PushSubscribeError(
      "existing_subscription_conflict",
      "An older subscription is in the way. Refresh the page and try again. If that fails, clear the site's notification permission and retry.",
      err,
    );
  }
  if (name === "NotSupportedError") {
    return new PushSubscribeError(
      "unsupported_browser",
      "This browser does not support web push. Try the latest Chrome, Edge, or Firefox.",
      err,
    );
  }
  return new PushSubscribeError(
    "unknown",
    `Could not subscribe to push notifications (${name || "unknown error"}). Try refreshing the page.`,
    err,
  );
}

/**
 * Ensures the current logged-in user is subscribed to web-push on this device.
 *
 * Throws PushSubscribeError on failure (with .code + friendly .message).
 * Returns true on success.
 *
 * Safe to call on every app load; idempotent.
 */
export async function ensurePushSubscription({ requestPermission = false } = {}) {
  if (!pushSupported()) {
    throw new PushSubscribeError("unsupported_browser",
      "This browser does not support web push notifications.");
  }

  // Permission state
  let perm = Notification.permission;
  if (perm === "denied") {
    throw new PushSubscribeError("permission_denied",
      "Notifications are blocked for this site. Enable them in your browser address-bar lock icon, then try again.");
  }
  if (perm === "default") {
    if (!requestPermission) return false; // silent no-op on app boot
    perm = await Notification.requestPermission();
    if (perm === "denied") {
      throw new PushSubscribeError("permission_denied",
        "Notifications were blocked. Enable them in your browser address-bar lock icon, then try again.");
    }
    if (perm !== "granted") {
      throw new PushSubscribeError("permission_dismissed",
        "Notification permission was not granted. Click 'Allow' when your browser asks.");
    }
  }

  const reg = await registerServiceWorker();
  if (!reg) {
    throw new PushSubscribeError("sw_register_failed",
      "Service worker could not register. This is usually caused by an outdated browser cache. Try a hard refresh (Cmd-Shift-R / Ctrl-Shift-R).");
  }
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();

  if (!sub) {
    // Fetch VAPID public key from backend (unauthenticated endpoint).
    let vapidKey = "";
    try {
      const r = await api.get("/push/vapid-public-key");
      vapidKey = r.data?.vapid_public_key || "";
    } catch (err) {
      // Surface the specific failure reason so the user (or support) can
      // tell whether it's a server-side missing-config (503) vs network
      // outage vs CORS issue. The vague "try again in a minute" message
      // was producing zero actionable signal during outages (May 30 batch
      // bug report #1).
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || "";
      const reason =
        code === 503
          ? "Push notifications are not configured on this server yet. Reach support@spectralearn.co - we will switch them on."
          : code
          ? `Couldn't fetch the push key from the server (HTTP ${code}). ${detail}`
          : `Couldn't fetch the push key from the server. Check your internet connection and try again. (${detail || "network error"})`;
      throw new PushSubscribeError("vapid_fetch_failed", reason, err);
    }
    if (!vapidKey) {
      throw new PushSubscribeError("vapid_not_configured",
        "Push notifications are not configured on the server right now. Email support@spectralearn.co.");
    }

    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Push subscribe failed", err);
      throw diagnoseSubscribeError(err);
    }
  }

  // Persist subscription on backend (upsert  -  safe to call repeatedly).
  try {
    const json = sub.toJSON();
    await api.post("/push/subscribe", {
      endpoint: json.endpoint,
      keys: json.keys,
      expirationTime: json.expirationTime || null,
    });
    return true;
  } catch (err) {
    throw new PushSubscribeError("backend_persist_failed",
      "Subscribed in the browser, but couldn't save the subscription on the server. Try again.", err);
  }
}

export async function unsubscribePush() {
  try {
    const sub = await currentPushSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      try { await api.post("/push/unsubscribe", { endpoint }); } catch { /* noop */ }
    }
    return true;
  } catch {
    return false;
  }
}
